import { useEffect, useRef, useState } from "react";
import {
    Users, FileSearch, Bot, Boxes, Search, RotateCcw, Check, ArrowRight,
} from "lucide-react";
import CandidateRankingPanel from "../mockups/CandidateRankingPanel";
import DocExtractionPanel from "../mockups/DocExtractionPanel";
import AgentRunPanel from "../mockups/AgentRunPanel";
import EngineeringSprintPanel from "../mockups/EngineeringSprintPanel";
import SearchResultsPanel from "../mockups/SearchResultsPanel";
import RoadmapCapture from "./RoadmapCapture";

const WORKFLOWS = [
    {
        id: "hiring",
        label: "Hiring",
        icon: Users,
        service: "Talent Solutions",
        input: { title: "1,204 inbound résumés", sub: "Role: Senior ML Engineer · remote" },
        steps: ["Parsing résumés", "Building skill graph", "Scoring against role context", "Ranking & shortlisting"],
        metric: { v: "11 days → 48h", l: "time to shortlist" },
        Panel: CandidateRankingPanel,
    },
    {
        id: "documents",
        label: "Documents",
        icon: FileSearch,
        service: "AI Solutions",
        input: { title: "14-page loan agreement", sub: "Format: scanned PDF + email" },
        steps: ["Splitting & OCR", "Classifying document", "Extracting fields", "Confidence scoring & routing"],
        metric: { v: "5× throughput", l: "document intake" },
        Panel: DocExtractionPanel,
    },
    {
        id: "agents",
        label: "Agents",
        icon: Bot,
        service: "AI Solutions",
        input: { title: "Employee HR request", sub: "“How much parental leave do I have?”" },
        steps: ["Classifying intent", "Retrieving policy", "Calling HR systems", "Drafting & filing response"],
        metric: { v: "1,200+ hrs / mo", l: "reclaimed" },
        Panel: AgentRunPanel,
    },
    {
        id: "engineering",
        label: "Engineering",
        icon: Boxes,
        service: "Product Engineering",
        input: { title: "AI copilot · product spec", sub: "Series B SaaS · embed in core app" },
        steps: ["Framing & discovery", "Designing UX", "Building behind flags", "Eval & GA rollout"],
        metric: { v: "9 weeks", l: "spec to GA" },
        Panel: EngineeringSprintPanel,
    },
    {
        id: "search",
        label: "Search",
        icon: Search,
        service: "AI Solutions",
        input: { title: "“comfy headphones for long flights”", sub: "Catalog: 10M+ rows" },
        steps: ["Understanding intent", "Semantic retrieval", "Personalised ranking", "Returning results"],
        metric: { v: "+18% conversion", l: "long-tail search" },
        Panel: SearchResultsPanel,
    },
];

const STEP_MS = 720;

export default function WorkflowSimulator() {
    const [active, setActive] = useState("hiring");
    const [runKey, setRunKey] = useState(0);
    const [step, setStep] = useState(0);
    const timer = useRef(null);

    const wf = WORKFLOWS.find((w) => w.id === active);
    const total = wf.steps.length;
    const done = step >= total;

    useEffect(() => {
        setStep(0);
        clearInterval(timer.current);
        timer.current = setInterval(() => {
            setStep((s) => {
                if (s >= total) {
                    clearInterval(timer.current);
                    return s;
                }
                return s + 1;
            });
        }, STEP_MS);
        return () => clearInterval(timer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, runKey]);

    const Panel = wf.Panel;

    return (
        <section
            className="pqx-section pqx-dark-panel relative overflow-hidden"
            data-testid="home-workflow-simulator"
        >
            <div className="absolute inset-0 pqx-grid-bg-dark opacity-40" />
            <div
                className="absolute -top-24 left-1/2 -translate-x-1/2 w-[680px] h-[420px] rounded-full blur-3xl pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(255,64,64,0.16), transparent 65%)" }}
            />

            <div className="pqx-container relative z-10">
                <div className="max-w-3xl">
                    <span className="pqx-eyebrow !text-[#FF4040]">See the platform think</span>
                    <h2 className="pqx-h2 mt-4 text-white">
                        Watch an AI workflow run — end to end.
                    </h2>
                    <p className="mt-4 text-white/68 text-[15px] md:text-base leading-relaxed max-w-2xl">
                        The same patterns we ship into production, simulated live. Pick a workflow
                        and watch raw input become a measurable, audited result.
                    </p>
                </div>

                {/* Tabs */}
                <div className="mt-6 flex flex-wrap gap-2" role="tablist" data-testid="simulator-tabs">
                    {WORKFLOWS.map((w) => {
                        const Icon = w.icon;
                        const isActive = w.id === active;
                        return (
                            <button
                                key={w.id}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                data-testid={`simulator-tab-${w.id}`}
                                onClick={() => setActive(w.id)}
                                className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-[13px] font-semibold transition-all ${isActive
                                        ? "bg-[#FF4040] text-white shadow-[0_10px_24px_-10px_rgba(255,64,64,0.6)]"
                                        : "bg-white/8 text-white/70 hover:bg-white/14 hover:text-white border border-white/10"
                                    }`}
                            >
                                <Icon className="w-4 h-4" />
                                {w.label}
                            </button>
                        );
                    })}
                </div>

                <div className="mt-6 grid lg:grid-cols-[0.85fr_1.15fr] gap-5 md:gap-6 items-stretch">
                    {/* Control / process column */}
                    <div className="pqx-glass-dark rounded-2xl p-5 flex flex-col">
                        <p className="text-[10px] uppercase tracking-[0.18em] text-white/45">Input</p>
                        <div className="mt-2 rounded-xl bg-white/6 border border-white/10 p-4">
                            <p className="text-[15px] font-semibold text-white leading-snug">{wf.input.title}</p>
                            <p className="mt-1 text-[12.5px] text-white/55">{wf.input.sub}</p>
                        </div>

                        <p className="mt-6 text-[10px] uppercase tracking-[0.18em] text-white/45">Pipeline</p>
                        <ol className="mt-3 space-y-2.5 flex-1" data-testid="simulator-steps">
                            {wf.steps.map((s, i) => {
                                const reached = step > i;
                                const running = step === i && !done;
                                return (
                                    <li key={s} className="flex items-center gap-3">
                                        <span
                                            className={`w-6 h-6 grid place-items-center rounded-full shrink-0 transition-all ${reached
                                                    ? "bg-[#FF4040] text-white"
                                                    : running
                                                        ? "bg-white/10 text-[#FF4040]"
                                                        : "bg-white/6 text-white/40"
                                                }`}
                                        >
                                            {reached ? (
                                                <Check className="w-3.5 h-3.5" />
                                            ) : running ? (
                                                <span className="w-3.5 h-3.5 rounded-full border-2 border-[#FF4040]/30 border-t-[#FF4040] pqx-anim-spin-ring" />
                                            ) : (
                                                <span className="font-mono text-[10px]">{i + 1}</span>
                                            )}
                                        </span>
                                        <span
                                            className={`text-[13.5px] transition-colors ${reached || running ? "text-white" : "text-white/45"
                                                }`}
                                        >
                                            {s}
                                        </span>
                                    </li>
                                );
                            })}
                        </ol>

                        <div className="mt-5 pt-5 border-t border-white/10 flex items-center justify-between">
                            <div>
                                <p className="pqx-stat-num text-2xl text-[#FF4040]">{wf.metric.v}</p>
                                <p className="text-[11px] text-white/55 uppercase tracking-[0.14em] mt-1">{wf.metric.l}</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setRunKey((k) => k + 1)}
                                data-testid="simulator-replay"
                                className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-[13px] font-semibold bg-white/10 text-white hover:bg-white/16 border border-white/12 transition-colors"
                            >
                                <RotateCcw className="w-4 h-4" />
                                Replay
                            </button>
                        </div>
                    </div>

                    {/* Output column */}
                    <div className="relative rounded-2xl bg-white/[0.03] border border-white/10 p-4 md:p-6 flex items-center justify-center min-h-[300px]">
                        <div
                            className="absolute top-4 right-4 inline-flex items-center gap-1.5 text-[11px] font-mono"
                            data-testid="simulator-status"
                        >
                            <span className={`w-1.5 h-1.5 rounded-full ${done ? "bg-[#FF4040]" : "bg-[#FF4040] pqx-anim-blink"}`} />
                            <span className="text-white/60">{done ? "result ready" : "processing…"}</span>
                        </div>

                        {done ? (
                            <div className="w-full max-w-md pqx-pop-in" data-testid="simulator-output">
                                <Panel className="scale-[0.92] sm:scale-95 origin-center" />
                                <p className="mt-3 text-center text-[12px] text-white/55 inline-flex items-center gap-1.5 justify-center w-full">
                                    Raw input <ArrowRight className="w-3.5 h-3.5 text-[#FF4040]" /> production-grade outcome
                                </p>
                            </div>
                        ) : (
                            <div className="text-center" data-testid="simulator-loading">
                                <span className="mx-auto block w-12 h-12 rounded-full border-2 border-white/10 border-t-[#FF4040] pqx-anim-spin-ring" />
                                <p className="mt-5 text-sm text-white/60">{wf.steps[Math.min(step, total - 1)]}…</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Connected Horizontal Flow for Desktop / Vertical for Mobile */}
                <div className="mt-12 pt-10 border-t border-white/10">
                    <p className="text-center text-[10.5px] uppercase tracking-[0.2em] text-[#FF4040] font-semibold">
                        Operating System Flow
                    </p>
                    <h3 className="text-center text-xl sm:text-2xl font-bold text-white mt-3">
                        How the PerceptIQX engine orchestrates delivery
                    </h3>
                    <p className="text-center text-sm text-white/55 mt-2 max-w-xl mx-auto">
                        The same operating model can support staffing, document intelligence, workflow automation, product engineering, and enterprise operations.
                    </p>

                    <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-6 relative">
                        {/* Desktop connecting lines */}
                        <div className="hidden lg:block absolute top-[24px] left-[10%] right-[10%] h-[1px] bg-gradient-to-r from-[#FF4040]/10 via-[#FF4040]/50 to-[#FF4040]/10 z-0" />

                        {[
                            {
                                step: "01",
                                label: "Request received",
                                desc: "A role, workflow, document set, product idea, or automation need enters the system.",
                            },
                            {
                                step: "02",
                                label: "AI parses context",
                                desc: "Skills, domain, data, requirements, risks, and delivery intent are extracted.",
                            },
                            {
                                step: "03",
                                label: "Relevant matches found",
                                desc: "Candidates, documents, workflows, systems, or accelerators are matched.",
                            },
                            {
                                step: "04",
                                label: "Human review",
                                desc: "Recruiters, engineers, SMEs, or business reviewers validate the output.",
                            },
                            {
                                step: "05",
                                label: "Output delivered",
                                desc: "Shortlist, automation, product action, dashboard, or implementation plan is shipped.",
                            },
                        ].map((s, idx) => (
                            <div key={s.step} className="relative z-10 flex flex-col items-center text-center group">
                                {/* Step number node */}
                                <div className="w-12 h-12 rounded-full bg-[#161616] border border-white/12 flex items-center justify-center text-sm font-bold text-white group-hover:border-[#FF4040] group-hover:shadow-[0_0_15px_rgba(255,64,64,0.3)] transition-all duration-300">
                                    <span className="text-[#FF4040]">{s.step}</span>
                                </div>
                                <h4 className="mt-4 text-[15px] font-semibold text-white group-hover:text-[#FF4040] transition-colors">
                                    {s.label}
                                </h4>
                                <p className="mt-2 text-[12.5px] text-white/60 leading-relaxed max-w-[200px] lg:max-w-none">
                                    {s.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Lead magnet — pinned to the simulator */}
                <RoadmapCapture key={active} label={wf.label} service={wf.service} />
            </div>
        </section>
    );
}



